/**
 * Pure helpers used across the Phase 9 (self-learning) surface — no React,
 * easy to unit-test, no side effects.
 *
 * Why a separate file: the route components import these to render the
 * winner badge and the cluster colours, but tests want to assert the
 * logic without mounting an entire TanStack router + QueryClient stack.
 */
import type { AbTestEvaluation, AbTestStatus } from '@chaos/types/learning';

/** Statuses that mean the experiment is irreversibly settled. */
export const FINAL_AB_STATUSES = new Set<AbTestStatus>(['promoted', 'rejected', 'aborted']);

/** Statuses that mean the experiment is still gathering data. */
export const ACTIVE_AB_STATUSES = new Set<AbTestStatus>(['proposed', 'running']);

/**
 * Decide which variant won given the evaluation summary.
 *
 *   - While the experiment is *not* final AND not significant → return null
 *     (both variants stay gray per the design rules).
 *   - Positive relative lift → candidate 'B' wins.
 *   - Negative relative lift → control 'A' wins.
 *   - Otherwise null.
 */
export function inferAbWinner(
  evaluation: AbTestEvaluation | null | undefined,
  isFinal: boolean,
): 'A' | 'B' | null {
  if (!evaluation) return null;
  if (!isFinal && !evaluation.significant) return null;
  if (evaluation.relativeLift == null) return null;
  if (evaluation.relativeLift > 0) return 'B';
  if (evaluation.relativeLift < 0) return 'A';
  return null;
}

/**
 * Decide the tone for a pattern cluster's success-rate badge.
 *   ≥ 0.8 → success
 *   ≥ 0.5 → warning
 *   < 0.5 → error
 *   null → muted
 *
 * Wraps the shared `effectivenessTone` semantics so consumers of patterns
 * can import a single helper without thinking about the formatter
 * indirection.
 */
export type ClusterTone = 'success' | 'warning' | 'error' | 'muted';

export function clusterTone(successRate: number | null | undefined): ClusterTone {
  if (successRate == null || !Number.isFinite(successRate)) return 'muted';
  if (successRate >= 0.8) return 'success';
  if (successRate >= 0.5) return 'warning';
  return 'error';
}
